import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { assertOk, isErr } from "@/lib/errors";
import { volume } from "@/lib/volume-api-2";
import { zapier } from "@/lib/zapier-api";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";
import { toast } from "sonner";
import { zodValidator } from "@tanstack/zod-adapter";
import { phoneNumberValidator } from "@/lib/validation";
import { logger } from "@/lib/logger";

export const Route = createFileRoute("/auth/_auth-layout/signup_")({
  component: RouteComponent,
  validateSearch: zodValidator(
    z.object({
      utm_source: z.string().optional(),
      utm_medium: z.string().optional(),
      utm_campaign: z.string().optional(),
    }),
  ),
});

const signUpFormSchema = z.object({
  email: z
    .email({ message: "Please enter a valid email address" })
    .toLowerCase(),
  company: z.string().nonempty({ message: "Company name is required" }),
  fullName: z.string().nonempty({ message: "Full name is required" }),
  phoneNumber: phoneNumberValidator,
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
});

function RouteComponent() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/auth/_auth-layout/signup_" });
  const form = useForm({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: {
      email: "",
      company: "",
      fullName: "",
      phoneNumber: "",
      utm_source: searchParams.utm_source,
      utm_medium: searchParams.utm_medium,
      utm_campaign: searchParams.utm_campaign,
    }, // Just so react doesn't cry of uncontrolled inputs
  });

  const signUpMutation = useMutation({
    mutationKey: ["signUp"],
    mutationFn: (data: z.infer<typeof signUpFormSchema>) => {
      const volumeResponse = volume.merchant.CreateMerchantAccount({
        companyName: data.company,
        email: data.email,
        fullName: data.fullName,
        phoneNumber: data.phoneNumber,
      });

      const zapierResponse = zapier.notify.Trigger({
        email: data.email,
        companyName: data.company,
        name: data.fullName,
        phoneNumber: data.phoneNumber,
        utm_medium: data.utm_medium,
        utm_source: data.utm_source,
        utm_campaign: data.utm_campaign,
      });

      assertOk(volumeResponse);

      if (isErr(zapierResponse)) {
        logger.error("Error sending zapier webhook", {
          zapierResponse,
        });
      }

      return volumeResponse;
    },
  });

  const onSubmit = async (data: z.infer<typeof signUpFormSchema>) => {
    await signUpMutation.mutateAsync(data, {
      onSettled: (data) => {
        if (isErr(data)) {
          if (data.type === "ACCOUNT_EXISTS") {
            toast.error(
              <div>
                An account with this email already exists, please{" "}
                <Link className="underline" to="/auth/signin">
                  Sign In.
                </Link>
              </div>,
            );

            return;
          }
          toast.error("There was an error signing up, please try again.");
        } else {
          navigate({ to: "/auth/signup/success" });
        }
      },
      onError: () => {
        toast.error("There was an error signing up, please try again.");
      },
    });
  };
  return (
    <div className="flex w-full flex-col  items-center rounded-3xl bg-white text-left">
      <img
        src="/volume-logo.svg"
        className="self-center"
        width={200}
        height={200}
        alt="Volume logo"
      />
      <h1 className="mt-8 px-8 text-center text-3xl font-extrabold">
        Create a Volume account
      </h1>
      <Form {...form}>
        <form
          className="mt-5 flex w-full flex-col items-center"
          onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
        >
          <div className=" flex w-full max-w-xl flex-col items-center px-8 text-left">
            <div className="grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Enter your email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-1 grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">
                      Company Name
                    </FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="eg. Volume" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-1 grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">Full Name</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="eg. John Smith"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-1 grid w-full items-center gap-1.5">
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-semibold">
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="eg. +44 123 123 123"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button className="w-full p-6 text-lg mt-4" type="submit">
              {signUpMutation.isPending ? (
                <Spinner height={48} width={48} white={true} />
              ) : (
                <>Sign Up</>
              )}
            </Button>
          </div>
        </form>
      </Form>
      <div className="m-2 self-center">
        Already have an account?{" "}
        <a href="/auth/signin" className="text-[#6829C5]">
          Sign in
        </a>
      </div>
      <div className="self-center">
        By clicking Register, you agree to our{" "}
        <a
          target="_blank"
          href="https://www.getvolume.com/terms-and-conditions"
          className="text-[#6829C5]"
        >
          Terms and Conditions
        </a>{" "}
        and{" "}
        <a
          target="_blank"
          href="https://www.getvolume.com/privacy"
          className="text-[#6829C5]"
        >
          Privacy Policy
        </a>
      </div>
    </div>
  );
}
